import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { Eyebrow, Reveal } from "../components/Reveal";
import { ArrowLeft, CalendarDays } from "lucide-react";
import Seo, { SITE_URL } from "../components/Seo";

import API from "../api";
const SITE = SITE_URL || "https://www.collanmoreislandlodge.com";

// Body paragraphs support simple [text](url) links.
function Paragraph({ text }) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [, label, href] = m;
    if (href.startsWith("/")) {
      parts.push(
        <Link key={key++} to={href} className="text-gold-2 underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold">
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="text-gold-2 underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold">
          {label}
        </a>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <p className="mt-6 text-base font-light leading-relaxed text-ivory/80">{parts}</p>;
}

export default function JournalPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    setPost(null);
    setMissing(false);
    fetch(`${API}/api/journal/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => alive && setPost(d.post))
      .catch(() => alive && setMissing(true));
    window.scrollTo(0, 0);
    return () => {
      alive = false;
    };
  }, [slug]);

  const jsonLd = post
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.meta_description,
          ...(post.image ? { image: post.image.startsWith("http") ? post.image : `${SITE}${post.image}` } : {}),
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { "@type": "Organization", name: "Collanmore Island Lodge", url: `${SITE}/` },
          publisher: { "@type": "Organization", name: "Collanmore Island Lodge", url: `${SITE}/` },
          mainEntityOfPage: `${SITE}/journal/${post.slug}/`,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Island Journal", item: `${SITE}/journal/` },
            { "@type": "ListItem", position: 3, name: post.title, item: `${SITE}/journal/${post.slug}/` },
          ],
        },
      ]
    : [];

  return (
    <PageLayout
      seo={{
        title: post ? `${post.title} | Collanmore Island Lodge` : "Island Journal | Collanmore Island Lodge",
        description: post ? post.meta_description : "Guides and stories from Collanmore Island Lodge in Clew Bay, County Mayo.",
        path: `/journal/${slug}/`,
        image: post && post.image ? (post.image.startsWith("http") ? post.image : `${SITE}${post.image}`) : undefined,
        jsonLd,
      }}
    >
      <main className="bg-navy pb-24 pt-36 md:pt-44" data-testid="journal-post-page">
        <div className="mx-auto max-w-[760px] px-5 md:px-10">
          <Link to="/journal/" data-testid="journal-back-link" className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Island Journal
          </Link>

          {missing && (
            <div className="mt-14 border border-ivory/15 bg-navy-2 p-10 text-center">
              <h1 className="font-display text-3xl text-ivory">This article has drifted away</h1>
              <p className="mt-4 text-sm font-light text-ivory/70">It may have been renamed or unpublished.</p>
              <Link to="/journal/" className="mt-6 inline-block bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-widest2 text-navy">
                Back to the Journal
              </Link>
            </div>
          )}

          {post && (
            <article className="mt-10">
              <Reveal>
                <Eyebrow light>{new Date(post.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })}</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-5 font-display text-4xl font-medium leading-tight text-ivory md:text-5xl" data-testid="journal-post-title">
                  {post.title}
                </h1>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-5 border-l-2 border-gold pl-5 text-base font-light italic leading-relaxed text-ivory/70">{post.excerpt}</p>
              </Reveal>
              {post.image && (
                <Reveal delay={0.18}>
                  <img src={post.image.startsWith("http") ? post.image : `${SITE}${post.image}`} alt={post.title} className="mt-10 aspect-[3/2] w-full object-cover" />
                </Reveal>
              )}
              <Reveal delay={0.2}>
                <div data-testid="journal-post-body" className="mt-4">
                  {post.body.map((para, i) => (
                    <Paragraph key={i} text={para} />
                  ))}
                </div>
              </Reveal>
              <div className="mt-14 border border-gold/30 bg-navy-2 p-8 text-center" data-testid="journal-cta">
                <p className="font-display text-2xl text-ivory">Imagine your group out there</p>
                <p className="mt-3 text-sm font-light text-ivory/70">One island in Clew Bay, exclusively yours — up to 22 guests.</p>
                <Link to="/contact/" data-testid="journal-enquire-button" className="mt-6 inline-block bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2">
                  Check Availability
                </Link>
              </div>
            </article>
          )}

          {!post && !missing && (
            <p className="mt-16 text-center text-xs uppercase tracking-widest2 text-ivory/50">Loading the article…</p>
          )}
        </div>
      </main>
    </PageLayout>
  );
}